/**
 * ProfileSetupSheet - Configuration complète du profil utilisateur ADHD-friendly
 *
 * Interface simple et progressive pour configurer :
 * - Informations de base (prénom, ville, horaires)
 * - Hobbies et centres d'intérêt
 * - Lieux favoris à proximité
 * - Situation professionnelle
 * - Santé et médicaments
 * - Préférences ADHD
 */

import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User, MapPin, Heart, Sun, Moon, Plus, X, Check,
  ChevronRight, ChevronLeft, Briefcase, Pill, Brain, Target, Phone, Mail
} from 'lucide-react';
import { BottomSheet } from '../ui/BottomSheet';

// Suggestions de hobbies avec icônes
const HOBBY_SUGGESTIONS = [
  { id: 'sport', label: 'Sport', emoji: '🏃' },
  { id: 'musique', label: 'Musique', emoji: '🎵' },
  { id: 'lecture', label: 'Lecture', emoji: '📚' },
  { id: 'jeux', label: 'Jeux vidéo', emoji: '🎮' },
  { id: 'photo', label: 'Photo', emoji: '📷' },
  { id: 'art', label: 'Art/Dessin', emoji: '🎨' },
  { id: 'cuisine', label: 'Cuisine', emoji: '👨‍🍳' },
  { id: 'cafe', label: 'Cafés/Restos', emoji: '☕' },
  { id: 'cinema', label: 'Cinéma/Séries', emoji: '🎬' },
  { id: 'nature', label: 'Nature/Rando', emoji: '🌲' },
  { id: 'yoga', label: 'Yoga/Méditation', emoji: '🧘' },
  { id: 'shopping', label: 'Shopping', emoji: '🛍️' },
];

// Types de lieux
const PLACE_TYPES = [
  { id: 'sport', label: 'Sport', emoji: '🏟️', placeholder: 'Ex: Stade Jean Bouin' },
  { id: 'courses', label: 'Courses', emoji: '🛒', placeholder: 'Ex: Carrefour City' },
  { id: 'sante', label: 'Santé', emoji: '🏥', placeholder: 'Ex: Pharmacie du Centre' },
  { id: 'loisirs', label: 'Loisirs', emoji: '🎬', placeholder: 'Ex: Cinéma UGC' },
  { id: 'parc', label: 'Parc', emoji: '🌳', placeholder: 'Ex: Parc Montsouris' },
  { id: 'travail', label: 'Travail', emoji: '🏢', placeholder: 'Ex: Bureau principal' },
];

// Types de travail
const WORK_TYPES = [
  { id: 'fixed', label: 'Horaires fixes', emoji: '🕐' },
  { id: 'flexible', label: 'Horaires flexibles', emoji: '⏰' },
  { id: 'freelance', label: 'Freelance', emoji: '💻' },
  { id: 'student', label: 'Étudiant(e)', emoji: '📚' },
  { id: 'unemployed', label: 'Sans emploi', emoji: '🔍' },
];

// Défis ADHD courants
const ADHD_CHALLENGES = [
  { id: 'concentration', label: 'Concentration', emoji: '🎯' },
  { id: 'organisation', label: 'Organisation', emoji: '📋' },
  { id: 'procrastination', label: 'Procrastination', emoji: '⏳' },
  { id: 'gestion_temps', label: 'Gestion du temps', emoji: '⏱️' },
  { id: 'transitions', label: 'Transitions', emoji: '🔄' },
  { id: 'motivation', label: 'Motivation', emoji: '🔥' },
];

// Stratégies de coping
const COPING_STRATEGIES = [
  { id: 'timer', label: 'Timer/Pomodoro', emoji: '⏲️' },
  { id: 'musique', label: 'Musique de fond', emoji: '🎧' },
  { id: 'listes', label: 'Listes/To-do', emoji: '✅' },
  { id: 'recompenses', label: 'Récompenses', emoji: '🎁' },
  { id: 'body_double', label: 'Body doubling', emoji: '👥' },
  { id: 'mouvement', label: 'Bouger/Marcher', emoji: '🚶' },
];

// Transports
const TRANSPORT_OPTIONS = [
  { id: 'walking', label: 'À pied', icon: '🚶' },
  { id: 'bike', label: 'Vélo', icon: '🚴' },
  { id: 'car', label: 'Voiture', icon: '🚗' },
  { id: 'public', label: 'Transports', icon: '🚇' },
];

const TOTAL_STEPS = 6;

/**
 * Composant principal de configuration du profil
 */
export function ProfileSetupSheet({
  isOpen,
  onClose,
  profile,
  onSaveProfile,
  isFirstSetup = false,
}) {
  // État local du formulaire - structure complète
  const [localProfile, setLocalProfile] = useState({
    firstName: '',
    email: '',
    phone: '',
    city: '',
    hobbies: [],
    nearbyPlaces: [],
    workSchedule: {
      type: 'flexible',
      startTime: '09:00',
      endTime: '18:00',
      workDays: [1, 2, 3, 4, 5],
      workFromHome: false,
      jobTitle: '',
    },
    healthInfo: {
      hasMedication: false,
      medicationTimes: [],
      medicationName: '',
      hasTherapy: false,
      therapyDay: '',
    },
    adhdInfo: {
      mainChallenges: [],
      copingStrategies: [],
    },
    preferences: {
      wakeTime: '07:00',
      sleepTime: '23:00',
      mealTimes: {
        breakfast: '07:30',
        lunch: '12:30',
        dinner: '19:30',
      },
      focusPeakTime: 'morning',
      needsBreaks: true,
      breakFrequency: 45,
    },
    transport: {
      preferredTransport: 'walking',
      hasVehicle: false,
      maxTravelTime: 30,
    },
    goals: {
      weeklyGoals: [],
      currentPriority: '',
    },
  });

  const [step, setStep] = useState(0);
  const [customHobby, setCustomHobby] = useState('');
  const [newPlace, setNewPlace] = useState({ name: '', type: 'courses' });
  const [newGoal, setNewGoal] = useState('');

  // Charger le profil existant depuis toutes les sources disponibles
  useEffect(() => {
    if (!isOpen) return;

    // Source 1: profile prop (from useAIWeekGenerator / Firestore)
    const fromProp = profile || {};

    // Source 2: localStorage profil
    let fromLocal = {};
    try {
      const saved = localStorage.getItem('adhd-user-profile');
      if (saved) fromLocal = JSON.parse(saved);
    } catch {}

    // Source 3: Firebase Auth user (nom, email)
    let fromAuth = {};
    try {
      const authRaw = localStorage.getItem('adhd-planner-google-auth');
      if (authRaw) {
        const { user } = JSON.parse(authRaw);
        if (user) {
          fromAuth.firstName = user.name?.split(' ')[0] || '';
          fromAuth.email = user.email || '';
        }
      }
    } catch {}

    // Source 4: Firebase Auth current user (si dispo)
    try {
      const fb = window.__FIREBASE_AUTH_USER__;
      if (!fb) {
        // Try import dynamically
        import('../../services/firebase').then(({ auth }) => {
          if (auth.currentUser) {
            const u = auth.currentUser;
            setLocalProfile(prev => ({
              ...prev,
              firstName: prev.firstName || u.displayName?.split(' ')[0] || '',
              email: prev.email || u.email || '',
            }));
          }
        }).catch(() => {});
      }
    } catch {}

    // Merge: prop > local > auth > defaults
    setLocalProfile(prev => ({
      ...prev,
      firstName: fromProp.firstName || fromLocal.firstName || fromAuth.firstName || prev.firstName,
      email: fromProp.email || fromLocal.email || fromAuth.email || prev.email,
      phone: fromProp.phone || fromLocal.phone || prev.phone,
      city: fromProp.city || fromLocal.city || prev.city,
      hobbies: fromProp.hobbies?.length > 0 ? [...fromProp.hobbies] : fromLocal.hobbies?.length > 0 ? [...fromLocal.hobbies] : prev.hobbies,
      nearbyPlaces: fromProp.nearbyPlaces?.length > 0 ? [...fromProp.nearbyPlaces] : fromLocal.nearbyPlaces?.length > 0 ? [...fromLocal.nearbyPlaces] : prev.nearbyPlaces,
      workSchedule: { ...prev.workSchedule, ...fromLocal.workSchedule, ...fromProp.workSchedule },
      healthInfo: { ...prev.healthInfo, ...fromLocal.healthInfo, ...fromProp.healthInfo },
      adhdInfo: { ...prev.adhdInfo, ...fromLocal.adhdInfo, ...fromProp.adhdInfo },
      preferences: {
        ...prev.preferences,
        ...fromLocal.preferences,
        ...fromProp.preferences,
        mealTimes: { ...prev.preferences.mealTimes, ...fromLocal.preferences?.mealTimes, ...fromProp.preferences?.mealTimes }
      },
      transport: { ...prev.transport, ...fromLocal.transport, ...fromProp.transport },
      goals: { ...prev.goals, ...fromLocal.goals, ...fromProp.goals },
    }));
  }, [profile, isOpen]);

  // Reset step
  useEffect(() => {
    if (isOpen) setStep(0);
  }, [isOpen]);

  // Helpers pour mise à jour
  const updateField = useCallback((field, value) => {
    setLocalProfile(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateNestedField = useCallback((parent, field, value) => {
    setLocalProfile(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  }, []);

  const toggleArrayItem = useCallback((parent, field, item) => {
    setLocalProfile(prev => {
      const arr = prev[parent]?.[field] || [];
      return {
        ...prev,
        [parent]: {
          ...prev[parent],
          [field]: arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]
        }
      };
    });
  }, []);

  const toggleHobby = useCallback((hobby) => {
    setLocalProfile(prev => ({
      ...prev,
      hobbies: prev.hobbies.includes(hobby)
        ? prev.hobbies.filter(h => h !== hobby)
        : [...prev.hobbies, hobby]
    }));
  }, []);

  const addCustomHobby = useCallback(() => {
    if (customHobby.trim() && !localProfile.hobbies.includes(customHobby.trim())) {
      setLocalProfile(prev => ({ ...prev, hobbies: [...prev.hobbies, customHobby.trim()] }));
      setCustomHobby('');
    }
  }, [customHobby, localProfile.hobbies]);

  const addPlace = useCallback(() => {
    if (newPlace.name.trim()) {
      setLocalProfile(prev => ({
        ...prev,
        nearbyPlaces: [...prev.nearbyPlaces, { name: newPlace.name.trim(), type: newPlace.type }]
      }));
      setNewPlace({ name: '', type: 'courses' });
    }
  }, [newPlace]);

  const removePlace = useCallback((index) => {
    setLocalProfile(prev => ({
      ...prev,
      nearbyPlaces: prev.nearbyPlaces.filter((_, i) => i !== index)
    }));
  }, []);

  const addGoal = useCallback(() => {
    if (newGoal.trim()) {
      setLocalProfile(prev => ({
        ...prev,
        goals: { ...prev.goals, weeklyGoals: [...(prev.goals.weeklyGoals || []), newGoal.trim()] }
      }));
      setNewGoal('');
    }
  }, [newGoal]);

  const removeGoal = useCallback((index) => {
    setLocalProfile(prev => ({
      ...prev,
      goals: { ...prev.goals, weeklyGoals: prev.goals.weeklyGoals.filter((_, i) => i !== index) }
    }));
  }, []);

  const toggleWorkDay = useCallback((day) => {
    setLocalProfile(prev => {
      const days = prev.workSchedule.workDays || [];
      return {
        ...prev,
        workSchedule: {
          ...prev.workSchedule,
          workDays: days.includes(day) ? days.filter(d => d !== day) : [...days, day]
        }
      };
    });
  }, []);

  const handleSave = useCallback(() => {
    onSaveProfile(localProfile);
    onClose();
  }, [localProfile, onSaveProfile, onClose]);

  const nextStep = () => setStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
  const prevStep = () => setStep(s => Math.max(s - 1, 0));
  const skipToEnd = () => setStep(TOTAL_STEPS - 1);

  const stepTitles = [
    { icon: User, title: 'Infos de base', color: 'purple' },
    { icon: Heart, title: 'Tes hobbies', color: 'pink' },
    { icon: MapPin, title: 'Tes lieux', color: 'emerald' },
    { icon: Briefcase, title: 'Travail', color: 'blue' },
    { icon: Brain, title: 'Profil ADHD', color: 'amber' },
    { icon: Target, title: 'Objectifs', color: 'cyan' },
  ];

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} height="92%">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex-shrink-0 px-4 pt-2 pb-4 border-b border-zinc-800">
          <div className="flex items-center gap-2 mb-3">
            {stepTitles.map((s, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-all ${
                  i <= step ? `bg-${s.color}-500` : 'bg-zinc-700'
                }`}
                style={{ backgroundColor: i <= step ? getColorHex(s.color) : undefined }}
              />
            ))}
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
                {React.createElement(stepTitles[step].icon, { className: 'w-5 h-5', style: { color: getColorHex(stepTitles[step].color) } })}
                {stepTitles[step].title}
              </h2>
              <p className="text-sm text-zinc-400 mt-0.5">
                Étape {step + 1}/{TOTAL_STEPS} - {isFirstSetup ? 'Tu peux passer les étapes' : 'Modifie tes infos'}
              </p>
            </div>
            <button onClick={onClose} className="p-2 text-zinc-400 hover:text-zinc-200">✕</button>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <AnimatePresence mode="wait">
            {/* Étape 0: Infos de base */}
            {step === 0 && (
              <StepContainer key="step0">
                <div className="space-y-4">
                  <Input
                    label="Comment tu t'appelles ?"
                    value={localProfile.firstName}
                    onChange={(v) => updateField('firstName', v)}
                    placeholder="Ton prénom"
                  />

                  <Input
                    label="Dans quelle ville ?"
                    value={localProfile.city}
                    onChange={(v) => updateField('city', v)}
                    placeholder="Ex: Paris, Lyon..."
                    icon={<MapPin className="w-4 h-4" />}
                  />

                  <Input
                    label="Ton email"
                    value={localProfile.email}
                    onChange={(v) => updateField('email', v)}
                    placeholder="nom@exemple.com"
                    icon={<Mail className="w-4 h-4" />}
                    type="email"
                  />

                  <Input
                    label="Ton téléphone"
                    value={localProfile.phone}
                    onChange={(v) => updateField('phone', v)}
                    placeholder="06 12 34 56 78"
                    icon={<Phone className="w-4 h-4" />}
                    type="tel"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <TimeInput
                      label="Réveil"
                      value={localProfile.preferences.wakeTime}
                      onChange={(v) => updateNestedField('preferences', 'wakeTime', v)}
                      icon={<Sun className="w-4 h-4" />}
                    />
                    <TimeInput
                      label="Coucher"
                      value={localProfile.preferences.sleepTime}
                      onChange={(v) => updateNestedField('preferences', 'sleepTime', v)}
                      icon={<Moon className="w-4 h-4" />}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <TimeInput
                      label="Petit-déj"
                      value={localProfile.preferences.mealTimes?.breakfast}
                      onChange={(v) => setLocalProfile(prev => ({
                        ...prev,
                        preferences: { ...prev.preferences, mealTimes: { ...prev.preferences.mealTimes, breakfast: v } }
                      }))}
                    />
                    <TimeInput
                      label="Déjeuner"
                      value={localProfile.preferences.mealTimes?.lunch}
                      onChange={(v) => setLocalProfile(prev => ({
                        ...prev,
                        preferences: { ...prev.preferences, mealTimes: { ...prev.preferences.mealTimes, lunch: v } }
                      }))}
                    />
                    <TimeInput
                      label="Dîner"
                      value={localProfile.preferences.mealTimes?.dinner}
                      onChange={(v) => setLocalProfile(prev => ({
                        ...prev,
                        preferences: { ...prev.preferences, mealTimes: { ...prev.preferences.mealTimes, dinner: v } }
                      }))}
                    />
                  </div>
                </div>
              </StepContainer>
            )}

            {/* Étape 1: Hobbies */}
            {step === 1 && (
              <StepContainer key="step1">
                <p className="text-sm text-zinc-500 mb-4">
                  Sélectionne ce que tu aimes faire. On les intégrera dans ton planning !
                </p>

                <div className="grid grid-cols-3 gap-2">
                  {HOBBY_SUGGESTIONS.map(hobby => (
                    <ToggleChip
                      key={hobby.id}
                      label={hobby.label}
                      emoji={hobby.emoji}
                      selected={localProfile.hobbies.includes(hobby.label)}
                      onClick={() => toggleHobby(hobby.label)}
                    />
                  ))}
                </div>

                {/* Custom hobbies */}
                {localProfile.hobbies.filter(h => !HOBBY_SUGGESTIONS.find(s => s.label === h)).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {localProfile.hobbies.filter(h => !HOBBY_SUGGESTIONS.find(s => s.label === h)).map(hobby => (
                      <span key={hobby} className="px-3 py-1 bg-pink-500/20 text-pink-400 rounded-full text-sm flex items-center gap-2">
                        {hobby}
                        <button onClick={() => toggleHobby(hobby)} className="hover:text-pink-200"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <input
                    type="text"
                    value={customHobby}
                    onChange={(e) => setCustomHobby(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCustomHobby()}
                    placeholder="Autre hobby..."
                    className="flex-1 p-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-500 text-sm focus:outline-none focus:border-pink-500"
                  />
                  <button onClick={addCustomHobby} disabled={!customHobby.trim()} className="px-3 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-500 disabled:opacity-50">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </StepContainer>
            )}

            {/* Étape 2: Lieux */}
            {step === 2 && (
              <StepContainer key="step2">
                <p className="text-sm text-zinc-500 mb-4">
                  Ajoute les endroits que tu fréquentes pour des suggestions personnalisées.
                </p>

                {localProfile.nearbyPlaces.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {localProfile.nearbyPlaces.map((place, index) => {
                      const placeType = PLACE_TYPES.find(p => p.id === place.type);
                      return (
                        <div key={index} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                          <span className="text-lg">{placeType?.emoji || '📍'}</span>
                          <div className="flex-1">
                            <div className="text-zinc-200 text-sm">{place.name}</div>
                            <div className="text-zinc-500 text-xs">{placeType?.label}</div>
                          </div>
                          <button onClick={() => removePlace(index)} className="p-1 text-zinc-500 hover:text-red-400">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {PLACE_TYPES.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setNewPlace(p => ({ ...p, type: type.id }))}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          newPlace.type === type.id
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500'
                            : 'bg-zinc-700/50 text-zinc-400 border border-transparent hover:bg-zinc-700'
                        }`}
                      >
                        {type.emoji} {type.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newPlace.name}
                      onChange={(e) => setNewPlace(p => ({ ...p, name: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && addPlace()}
                      placeholder={PLACE_TYPES.find(t => t.id === newPlace.type)?.placeholder}
                      className="flex-1 p-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-500 text-sm focus:outline-none focus:border-emerald-500"
                    />
                    <button onClick={addPlace} disabled={!newPlace.name.trim()} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 disabled:opacity-50">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </StepContainer>
            )}

            {/* Étape 3: Travail */}
            {step === 3 && (
              <StepContainer key="step3">
                <p className="text-sm text-zinc-500 mb-4">
                  Ta situation professionnelle pour adapter ton planning.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-zinc-400 block mb-2">Type de travail</label>
                    <div className="flex flex-wrap gap-2">
                      {WORK_TYPES.map(type => (
                        <ToggleChip
                          key={type.id}
                          label={type.label}
                          emoji={type.emoji}
                          selected={localProfile.workSchedule.type === type.id}
                          onClick={() => updateNestedField('workSchedule', 'type', type.id)}
                        />
                      ))}
                    </div>
                  </div>

                  {localProfile.workSchedule.type !== 'unemployed' && (
                    <>
                      <Input
                        label="Ton poste/métier"
                        value={localProfile.workSchedule.jobTitle}
                        onChange={(v) => updateNestedField('workSchedule', 'jobTitle', v)}
                        placeholder="Ex: Développeur, Étudiant en droit..."
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <TimeInput
                          label="Début"
                          value={localProfile.workSchedule.startTime}
                          onChange={(v) => updateNestedField('workSchedule', 'startTime', v)}
                        />
                        <TimeInput
                          label="Fin"
                          value={localProfile.workSchedule.endTime}
                          onChange={(v) => updateNestedField('workSchedule', 'endTime', v)}
                        />
                      </div>

                      <div>
                        <label className="text-sm text-zinc-400 block mb-2">Jours de travail</label>
                        <div className="flex gap-2">
                          {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((day, i) => (
                            <button
                              key={i}
                              onClick={() => toggleWorkDay(i === 6 ? 0 : i + 1)}
                              className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${
                                localProfile.workSchedule.workDays.includes(i === 6 ? 0 : i + 1)
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-zinc-800 text-zinc-400'
                              }`}
                            >
                              {day}
                            </button>
                          ))}
                        </div>
                      </div>

                      <Toggle
                        label="Télétravail"
                        checked={localProfile.workSchedule.workFromHome}
                        onChange={(v) => updateNestedField('workSchedule', 'workFromHome', v)}
                      />
                    </>
                  )}
                </div>
              </StepContainer>
            )}

            {/* Étape 4: Santé & ADHD */}
            {step === 4 && (
              <StepContainer key="step4">
                <p className="text-sm text-zinc-500 mb-4">
                  Ces infos restent privées et aident à créer un planning adapté.
                </p>

                <div className="space-y-5">
                  {/* Médicaments */}
                  <div className="bg-zinc-800/30 rounded-lg p-4 space-y-3">
                    <Toggle
                      label="Je prends des médicaments"
                      checked={localProfile.healthInfo.hasMedication}
                      onChange={(v) => updateNestedField('healthInfo', 'hasMedication', v)}
                      icon={<Pill className="w-4 h-4" />}
                    />

                    {localProfile.healthInfo.hasMedication && (
                      <>
                        <Input
                          label="Lesquels ?"
                          value={localProfile.healthInfo.medicationName}
                          onChange={(v) => updateNestedField('healthInfo', 'medicationName', v)}
                          placeholder="Ex: Ritaline, Concerta..."
                        />
                        <Input
                          label="Horaires de prise"
                          value={(localProfile.healthInfo.medicationTimes || []).join(', ')}
                          onChange={(v) => updateNestedField('healthInfo', 'medicationTimes', v.split(',').map(t => t.trim()).filter(Boolean))}
                          placeholder="Ex: 08:00, 14:00"
                        />
                      </>
                    )}
                  </div>

                  {/* Thérapie */}
                  <Toggle
                    label="J'ai un suivi psy/thérapie"
                    checked={localProfile.healthInfo.hasTherapy}
                    onChange={(v) => updateNestedField('healthInfo', 'hasTherapy', v)}
                  />

                  {/* Défis ADHD */}
                  <div>
                    <label className="text-sm text-zinc-400 block mb-2">Mes difficultés principales</label>
                    <div className="flex flex-wrap gap-2">
                      {ADHD_CHALLENGES.map(ch => (
                        <ToggleChip
                          key={ch.id}
                          label={ch.label}
                          emoji={ch.emoji}
                          selected={(localProfile.adhdInfo.mainChallenges || []).includes(ch.id)}
                          onClick={() => toggleArrayItem('adhdInfo', 'mainChallenges', ch.id)}
                          small
                        />
                      ))}
                    </div>
                  </div>

                  {/* Stratégies */}
                  <div>
                    <label className="text-sm text-zinc-400 block mb-2">Ce qui m'aide</label>
                    <div className="flex flex-wrap gap-2">
                      {COPING_STRATEGIES.map(st => (
                        <ToggleChip
                          key={st.id}
                          label={st.label}
                          emoji={st.emoji}
                          selected={(localProfile.adhdInfo.copingStrategies || []).includes(st.id)}
                          onClick={() => toggleArrayItem('adhdInfo', 'copingStrategies', st.id)}
                          small
                        />
                      ))}
                    </div>
                  </div>

                  {/* Transport */}
                  <div>
                    <label className="text-sm text-zinc-400 block mb-2">Transport préféré</label>
                    <div className="flex gap-2">
                      {TRANSPORT_OPTIONS.map(t => (
                        <button
                          key={t.id}
                          onClick={() => updateNestedField('transport', 'preferredTransport', t.id)}
                          className={`flex-1 p-3 rounded-lg text-center transition-all ${
                            localProfile.transport.preferredTransport === t.id
                              ? 'bg-cyan-500/20 border-2 border-cyan-500 text-cyan-400'
                              : 'bg-zinc-800/50 border-2 border-transparent text-zinc-400'
                          }`}
                        >
                          <div className="text-xl mb-1">{t.icon}</div>
                          <div className="text-xs">{t.label}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </StepContainer>
            )}

            {/* Étape 5: Objectifs */}
            {step === 5 && (
              <StepContainer key="step5">
                <p className="text-sm text-zinc-500 mb-4">
                  Qu'est-ce qui est important pour toi cette semaine ?
                </p>

                <div className="space-y-4">
                  <Input
                    label="Ta priorité du moment"
                    value={localProfile.goals.currentPriority}
                    onChange={(v) => updateNestedField('goals', 'currentPriority', v)}
                    placeholder="Ex: Chercher un emploi, Réviser les examens..."
                  />

                  <div>
                    <label className="text-sm text-zinc-400 block mb-2">Objectifs de la semaine</label>

                    {(localProfile.goals.weeklyGoals || []).length > 0 && (
                      <div className="space-y-2 mb-3">
                        {localProfile.goals.weeklyGoals.map((goal, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded-lg">
                            <Target className="w-4 h-4 text-cyan-400" />
                            <span className="flex-1 text-sm text-zinc-200">{goal}</span>
                            <button onClick={() => removeGoal(i)} className="p-1 text-zinc-500 hover:text-red-400">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newGoal}
                        onChange={(e) => setNewGoal(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addGoal()}
                        placeholder="Ex: Faire 3 séances de sport"
                        className="flex-1 p-2 bg-zinc-800/50 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-500 text-sm focus:outline-none focus:border-cyan-500"
                      />
                      <button onClick={addGoal} disabled={!newGoal.trim()} className="px-3 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-500 disabled:opacity-50">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Récap */}
                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg p-4 mt-6">
                    <h3 className="text-sm font-medium text-purple-300 mb-2">Récap de ton profil</h3>
                    <div className="text-xs text-zinc-400 space-y-1">
                      <p>Prénom: {localProfile.firstName || 'Non renseigné'}</p>
                      <p>Ville: {localProfile.city || 'Non renseignée'}</p>
                      <p>Hobbies: {localProfile.hobbies.length || 0} sélectionnés</p>
                      <p>Lieux: {localProfile.nearbyPlaces.length || 0} ajoutés</p>
                      <p>Travail: {WORK_TYPES.find(t => t.id === localProfile.workSchedule.type)?.label}</p>
                    </div>
                  </div>
                </div>
              </StepContainer>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-4 py-4 border-t border-zinc-800 bg-zinc-900/80">
          <div className="flex items-center gap-3">
            {step > 0 ? (
              <button onClick={prevStep} className="px-4 py-2.5 bg-zinc-800 text-zinc-300 rounded-lg hover:bg-zinc-700 flex items-center gap-2">
                <ChevronLeft className="w-4 h-4" />
                Retour
              </button>
            ) : (
              <button onClick={onClose} className="px-4 py-2.5 bg-zinc-800 text-zinc-400 rounded-lg hover:bg-zinc-700">
                Annuler
              </button>
            )}

            {step < TOTAL_STEPS - 1 && isFirstSetup && (
              <button onClick={skipToEnd} className="px-3 py-2.5 text-zinc-500 hover:text-zinc-300 text-sm">
                Passer
              </button>
            )}

            <button
              onClick={step < TOTAL_STEPS - 1 ? nextStep : handleSave}
              className={`flex-1 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                step < TOTAL_STEPS - 1
                  ? 'bg-purple-600 text-white hover:bg-purple-500'
                  : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-500 hover:to-pink-500'
              }`}
            >
              {step < TOTAL_STEPS - 1 ? (
                <>Continuer <ChevronRight className="w-4 h-4" /></>
              ) : (
                <><Check className="w-5 h-5" /> Enregistrer</>
              )}
            </button>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
}

// Composants utilitaires
function StepContainer({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      {children}
    </motion.div>
  );
}

function Input({ label, value, onChange, placeholder, icon, type = 'text' }) {
  return (
    <div>
      <label className="text-sm text-zinc-400 block mb-2 flex items-center gap-1">
        {icon}
        {label}
      </label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-purple-500"
      />
    </div>
  );
}

function TimeInput({ label, value, onChange, icon }) {
  return (
    <div>
      <label className="text-sm text-zinc-400 block mb-2 flex items-center gap-1">
        {icon}
        {label}
      </label>
      <input
        type="time"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg text-zinc-200 focus:outline-none focus:border-purple-500"
      />
    </div>
  );
}

function Toggle({ label, checked, onChange, icon }) {
  return (
    <label className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg cursor-pointer">
      <span className="text-sm text-zinc-300 flex items-center gap-2">
        {icon}
        {label}
      </span>
      <div className="relative">
        <input type="checkbox" checked={checked || false} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
        <div className="w-11 h-6 bg-zinc-700 rounded-full peer peer-checked:bg-purple-500 transition-colors" />
        <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform" />
      </div>
    </label>
  );
}

function ToggleChip({ label, emoji, selected, onClick, small }) {
  return (
    <button
      onClick={onClick}
      className={`
        ${small ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'}
        rounded-lg transition-all flex items-center gap-1.5
        ${selected
          ? 'bg-purple-500/20 border-2 border-purple-500 text-purple-300'
          : 'bg-zinc-800/50 border-2 border-transparent text-zinc-400 hover:bg-zinc-800'
        }
      `}
    >
      <span>{emoji}</span>
      <span>{label}</span>
      {selected && <Check className="w-3 h-3" />}
    </button>
  );
}

function getColorHex(color) {
  const colors = {
    purple: '#a855f7',
    pink: '#ec4899',
    emerald: '#10b981',
    blue: '#3b82f6',
    amber: '#f59e0b',
    cyan: '#06b6d4',
  };
  return colors[color] || '#a855f7';
}

export default ProfileSetupSheet;
